import { useRuntimeConfig } from '#app'
import { useLoggingControl } from './useLoggingControl'

export type LogLevel = 'debug' | 'log' | 'warn' | 'error'

interface LoggerOptions {
  isProd: boolean
}

const defaultOptions: LoggerOptions = {
  isProd: process.env.NODE_ENV === 'production',
}

// Singleton instance to maintain consistent state
let loggerInstance: ReturnType<typeof createLogger> | null = null

function createLogger(options: LoggerOptions = defaultOptions) {
  let shouldLog: (level: LogLevel) => boolean

  try {
    const { shouldLog: loggingControl } = useLoggingControl()
    shouldLog = loggingControl
  } catch (e) {
    // Fallback logging control if outside Nuxt context
    shouldLog = (level: LogLevel) => {
      if (options.isProd) {
        return level === 'error'
      }
      return true
    }
  }

  return {
    debug: (...args: unknown[]) => {
      if (shouldLog('debug')) {
        console.debug(...args)
      }
    },
    log: (...args: unknown[]) => {
      // Special handling for Pinia logs
      const isPiniaLog = args.some((arg) => typeof arg === 'string' && arg.includes('🍍'))
      if (isPiniaLog) {
        if (shouldLog('debug')) console.log(...args)
      } else {
        if (shouldLog('log')) console.log(...args)
      }
    },
    warn: (...args: unknown[]) => {
      if (shouldLog('warn')) {
        console.warn(...args)
      }
    },
    logError: (message: string, error?: unknown, ...args: unknown[]) => {
      if (shouldLog('error')) {
        console.error(message, error, ...args)
      }
    },
  }
}

// Get or create the singleton instance
const getLogger = () => {
  if (!loggerInstance) {
    let options = defaultOptions

    try {
      const config = useRuntimeConfig()
      options = {
        isProd: config.public?.isProd ?? defaultOptions.isProd,
      }
    } catch (e) {
      // Use default options if outside Nuxt context
      console.debug('Logger created outside Nuxt context, using default options')
    }

    loggerInstance = createLogger(options)
  }
  return loggerInstance
}

// Export the full logger if needed
export const useLogger = () => getLogger()

// Export simple global functions that match current usage
export const log = (...args: unknown[]) => getLogger().log(...args)
export const warn = (...args: unknown[]) => getLogger().warn(...args)
export const logError = (message: string, error?: unknown, ...args: unknown[]) =>
  getLogger().logError(message, error, ...args)
export const debug = (...args: unknown[]) => getLogger().debug(...args)

// Type augmentation for Nuxt
declare module '#app' {
  interface NuxtApp {
    log: (...args: unknown[]) => void
    warn: (...args: unknown[]) => void
    logError: (message: string, error?: unknown, ...args: unknown[]) => void
    debug: (...args: unknown[]) => void
  }
}

// Type augmentation for Vue
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    log: (...args: unknown[]) => void
    warn: (msg: string, ...args: unknown[]) => void
    logError: (message: string, error?: unknown, ...args: unknown[]) => void
    debug: (...args: unknown[]) => void
  }
}

// Type augmentation for global
declare global {
  interface Window {
    log: (...args: unknown[]) => void
    warn: (...args: unknown[]) => void
    logError: (message: string, error?: unknown, ...args: unknown[]) => void
    debug: (...args: unknown[]) => void
  }
}
